<script>
import { BModal } from 'bootstrap-vue';
import { VBTooltip } from 'bootstrap-vue';
import dayjs from '../../core/dayjs';
import { Alert } from '../../services/alert'
import IconPdfFile from '../icon/IconPdfFile.vue';
import IconPngFile from '../icon/IconPngFile.vue';
import IconJpgFile from '../icon/IconJpgFile.vue';
import IconGifFile from '../icon/IconGifFile.vue';
import IconXlsxFile from '../icon/IconXlsxFile.vue';

export default {
  components: {
    BModal,
    IconPdfFile,
    IconPngFile,
    IconJpgFile,
    IconGifFile,
    IconXlsxFile,
  },

  directives: {
    'b-tooltip': VBTooltip
  },

  props: {
    hash: {
      type: String,
      required: true,
    },
  },

  data () {
    return {
      files: [],
      currentPage: 0,
      lastPage: null,
      iconComponentsMap: {
        'pdf': 'IconTextFile',
        'mp3': 'IconSoundFile',
        'pdf': 'IconPdfFile',
        'png': 'IconPngFile',
        'jpg': 'IconJpgFile',
        'jpeg': 'IconJpgFile',
        'gif': 'IconGifFile',
        'xlsx': 'IconXlsxFile',
      },
      notAllowedExtensions: ['jpg', 'jpeg', 'png', 'mp4', 'webm', 'ogg'],
      isLoading: true,
      documentType: '',
      lastDocumentType: ''
    };
  },

  mounted () {
    Bus.$on('openDocsModal', (documentType) => {
      this.documentType = documentType;

      this.openModal();
    });
  },

  methods: {
    openModal () {
      this.$refs['lead-docs-modal'].show();
    },

    hideModal () {
      this.$refs['lead-docs-modal'].hide();
    },

    onModalHidden() {
      this.files = [];
      this.isLoading = true;
      this.currentPage = 0;
      this.lastPage = null;
    },

    getFiles () {
      const url = new URL(`${location.origin}/lead/documents/${this.hash}`);

      this.notAllowedExtensions.forEach((extension) => {
        url.searchParams.append('type[out][]', extension);
      });

      url.searchParams.append('page', this.currentPage + 1)
      url.searchParams.append('document_type', this.documentType);

      this.$http.get(url.toString()).then(({ body }) => {
        if (this.files.length && this.currentPage > 0) {
          this.files = this.files.concat(body.data);
        } else {
          this.files = body.data;
        }
        this.currentPage = body.current_page;
        this.lastPage = body.last_page;

        this.$nextTick(() => {
          this.$nextTick(this.assignTooltipsText);
        });
      }).catch(() => {
        Alert.error('Erro ao buscar arquivos');
      }).finally(() => {
        this.isLoading = false;
      });
    },

    completeFileName (file) {
      return `${file.title}.${file.extension}`;
    },

    handleModalShown () {
      this.getFiles();   
    },

    openFileModal (file) {
      Bus.$emit('openFileModal', file);
    },

    assignTooltipsText () {
      this.files = this.files.map((file, index) => {
        const fileElement = document.getElementById(`file-${index}`);
        const fileTextElement = document.getElementById(`file-text-${index}`);

        if (fileTextElement.offsetWidth <= fileElement.offsetWidth) {
          return file;
        }

        file.tooltipText = this.completeFileName(file);

        return file;
      });
    },

    fileDetails (file) {
      const details = [];

      if (file.pages) {
        const pages = `${file.pages} página${file.pages === 1 ? '' : 's'}`;
        details.push(pages);
      }

      if (file.size) {
        const formattedSize = this.formatFileSize(file.size);
        details.push(formattedSize);
      }

      if (file.extension) {
        const extension = file.extension.toUpperCase();
        details.push(extension);
      }

      return details;
    },

    formatDate (date) {
      if (!date) {
        return 'Data indefinida';
      }

      try {
        return dayjs(date).format('DD/MM/YYYY');
      } catch (error) {
        return 'Data inválida';
      }
    },

    formatFileSize(bytes) {
      if (bytes < 1024) {
        return bytes + ' B';
      } else if (bytes < 1024 * 1024) {
        return (bytes / 1024).toFixed(2) + ' KB';
      } else if (bytes < 1024 * 1024 * 1024) {
        return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
      } else {
        return (bytes / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
      }
    },

    iconComponentName (file) {
      const componentName = this.iconComponentsMap[file.extension];

      if (!componentName) {
        return 'IconFile';
      }

      return componentName;
    }
  },
};
</script>

<template>
  <BModal
    id="lead-docs-modal"
    ref="lead-docs-modal"
    hide-footer="hide-footer"
    centered
    @shown="handleModalShown"
    @hidden="onModalHidden()"
  >
    <template slot="modal-header">
      <h2 class="modal-title">
        DOCS
      </h2>

      <button
      class="close"
      type="button"
      data-dismiss="modal"
      aria-label="Close"
      @click="hideModal()"
    >
      <span aria-hidden="true" ><img
        class="mb-1"
        src="/build/icons/close@white.svg"
        alt="Fechar"
        width="14"
        height="13"
      ></span>
    </button>
    </template>
    <template slot="default">
      <div v-show="!isLoading || this.currentPage > 0" class="px-4">
        <a
          v-for="(file, index) in files"
          @click="openFileModal(file)"
          class="file"
        >
          <Component
            :is="iconComponentName(file)"
            :size="24"
          />
          <div class="file-info ml-2">
            <div
              :id="`file-${index}`"
              v-b-tooltip
              :title="file.tooltipText"
              class="file-name text-truncate"
            >
              <span
                :id="`file-text-${index}`"
                class="text-truncate"
              >
                {{ completeFileName(file) }}
              </span>
            </div>
            <div class="w-100 d-flex align-items-center justify-content-between">
              <div class="file-details">
                <span
                  v-for="(detail, index) in fileDetails(file)"
                >
                  {{ detail }} {{ index < fileDetails(file).length - 1 ? '&#x2022;' : '' }}
                </span>
              </div>
              <span>
                {{ formatDate(file.created_at) }}
              </span>
            </div>
          </div>
        </a>
        <div v-if="!files.length && !isLoading" class="no-data">
          <h3 class="mt-4">
            {{ __('Não há dados a serem exibidos') }}
          </h3>
        </div>
        <div v-if="currentPage < lastPage" class="load-more">
          <button
            v-if="!isLoading"
            @click="getFiles()"
          >
            {{ __('Ver mais') }}
          </button>
          <i v-else class="fa fa-circle-o-notch fa-spin" />
        </div>
      </div>
      <div v-show="!files.length && isLoading" class="spinner-container">
        <i class="fa fa-circle-o-notch fa-spin" />
      </div>
    </template>
  </BModal>
</template>

<style lang="scss">
  @import '../../../sass/variables';
  @import '../../../sass/mixins';

  #lead-docs-modal {
    .modal-dialog {
      min-width: 546px;
    }

    .modal-content {
      border-radius: 16px;
      overflow: hidden;
    }

    .modal-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 16px 24px;

      .modal-title {
        @include salesflow-text($font-weight: 600, $color: white);
      }
    }

    .modal-body {
      padding: 8px 0;
      max-height: 23.5rem;
      min-height: 23.5rem;
      overflow-y: scroll;
      background-color: $salesflow-bg-content;

      &::-webkit-scrollbar {
        display: none;
      }

      .file {
        display: flex;
        align-items: center;
        padding: 8px 16px;
        background-color: transparent;

        &:hover {
          cursor: pointer;
          background-color: $salesflow-outline-gray-light;
        }

        svg {
          min-width: 24px;
        }

        .file-info {
          display: flex;
          flex-direction: column;
          flex: 1;
          @include salesflow-label($color: $salesflow-font-gray);

          .file-name {
            max-width: 370px;
            @include salesflow-subtitle($color: $salesflow-font-dark);

            span {
              width: fit-content;
              max-width: 370px;
            }
          }

          .file-details {
            display: inline-block;
          }
        }
      }

      .no-data {
        h3 {
          text-align: center;
          @include salesflow-subtitle($font-weight: 500, $color: $salesflow-font-gray);
        }
      }

      .load-more {
        width: 100%;
        display: flex;
        align-self: center;
        justify-content: center;
        padding: 12px;

        button {
          background: transparent;
          border: 0px;
          outline: 0px;

          @include salesflow-text;

          &:hover {
            color: $salesflow-primary-hover;
          }
        }
      }

      .spinner-container {
        width: 100%;
        height: 23.5rem;
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }
  }
</style>
